import { useCallback, useDeferredValue, useMemo } from "react"

/**
 * Fast substring match for large combobox lists.
 * Base UI's default Collator `contains` does O(n) compares per character and
 * is noticeably slower than MUI's filter on multi-thousand option sets.
 */
export function fastComboboxFilter<T>(
  item: T,
  query: string,
  itemToStringLabel?: (item: T) => string
): boolean {
  if (!query) return true
  const label =
    itemToStringLabel?.(item) ??
    (typeof item === "string" ? item : String(item ?? ""))
  return label.toLowerCase().includes(query.toLowerCase())
}

type CreatableOption = {
  label: string
  inputValue?: string
}

/**
 * Stable `items` + deferred `filteredItems` for creatable comboboxes.
 * Keeps the root `items` reference stable (no per-keystroke array copy) and
 * defers filter work so typing stays responsive.
 */
export function useCreatableComboboxFilter<T extends CreatableOption>(
  items: T[],
  inputValue: string,
  createOption: (inputValue: string) => T
) {
  const deferredQuery = useDeferredValue(inputValue.trim())

  const filteredItems = useMemo(() => {
    const query = deferredQuery
    const queryLower = query.toLowerCase()

    const matched = query
      ? items.filter((item) => item.label.toLowerCase().includes(queryLower))
      : items

    if (query && !items.some((item) => item.label === query)) {
      return [...matched, createOption(query)]
    }

    return matched
  }, [items, deferredQuery, createOption])

  const filter = useCallback(
    (item: T, query: string) => fastComboboxFilter(item, query, (i) => i.label),
    []
  )

  return { filteredItems, filter, deferredQuery }
}
